import React, { useState } from 'react';
import { 
  PlayIcon, 
  PlusIcon, 
  TrashIcon,
  DocumentTextIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { ApiRequest } from '../../services/apiPlaygroundService';

interface RequestBuilderProps {
  request: ApiRequest;
  onChange: (request: ApiRequest) => void;
  onExecute: () => void;
  isLoading: boolean;
}

const RequestBuilder: React.FC<RequestBuilderProps> = ({
  request,
  onChange,
  onExecute,
  isLoading
}) => {
  const [activeTab, setActiveTab] = useState<'body' | 'headers' | 'params'>('body');

  const handleMethodChange = (method: string) => {
    onChange({ ...request, method });
  };

  const handleUrlChange = (url: string) => {
    onChange({ ...request, url });
  };

  const handleBodyChange = (body: string) => {
    onChange({ ...request, body });
  };

  const handleHeaderChange = (key: string, value: string) => {
    const newHeaders = { ...request.headers };
    if (value) {
      newHeaders[key] = value;
    } else {
      delete newHeaders[key];
    }
    onChange({ ...request, headers: newHeaders });
  };

  const addHeader = () => {
    const newHeaders = { ...request.headers, '': '' };
    onChange({ ...request, headers: newHeaders });
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...request.headers };
    delete newHeaders[key];
    onChange({ ...request, headers: newHeaders });
  };

  const formatJson = () => {
    if (request.body) {
      try {
        const parsed = JSON.parse(request.body);
        const formatted = JSON.stringify(parsed, null, 2);
        handleBodyChange(formatted);
      } catch (error) {
        // Invalid JSON, don't format
      }
    }
  };

  const methods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Request Builder</h3>
        <button
          onClick={onExecute}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Executing...
            </>
          ) : (
            <>
              <PlayIcon className="h-4 w-4 mr-2" />
              Send Request
            </>
          )}
        </button>
      </div>

      {/* Method and URL */}
      <div className="space-y-3">
        <div className="flex space-x-2">
          <select
            value={request.method}
            onChange={(e) => handleMethodChange(e.target.value)}
            className="block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            {methods.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={request.url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="Enter request URL"
            className="flex-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {[
            { id: 'body' as const, name: 'Body', icon: DocumentTextIcon },
            { id: 'headers' as const, name: 'Headers', icon: CogIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
              {tab.id === 'headers' && Object.keys(request.headers).length > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {Object.keys(request.headers).length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'body' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Request Body
              </label>
              {request.body && (
                <button
                  onClick={formatJson}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Format JSON
                </button>
              )}
            </div>
            <textarea
              value={request.body || ''}
              onChange={(e) => handleBodyChange(e.target.value)}
              placeholder="Enter request body (JSON)"
              rows={12}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
            />
            <p className="text-xs text-gray-500">
              Enter JSON data for POST/PUT requests
            </p>
          </div>
        )}

        {activeTab === 'headers' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Request Headers
              </label>
              <button
                onClick={addHeader}
                className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-3 w-3 mr-1" />
                Add Header
              </button>
            </div>
            
            <div className="space-y-2">
              {Object.entries(request.headers).map(([key, value], index) => (
                <div key={index} className="flex space-x-2">
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => {
                      const newHeaders = { ...request.headers };
                      delete newHeaders[key];
                      if (e.target.value) {
                        newHeaders[e.target.value] = value;
                      }
                      onChange({ ...request, headers: newHeaders });
                    }}
                    placeholder="Header name"
                    className="flex-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleHeaderChange(key, e.target.value)}
                    placeholder="Header value"
                    className="flex-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <button
                    onClick={() => removeHeader(key)}
                    className="p-2 text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            
            {Object.keys(request.headers).length === 0 && (
              <div className="text-center py-6 text-gray-500 text-sm">
                No headers added. Click "Add Header" to add custom headers.
              </div>
            )}
            
            <p className="text-xs text-gray-500">
              Note: X-API-Key header will be automatically added using your selected API key
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestBuilder;
